import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { http } from "@/lib/api";
import { readLocation } from "@/components/LocationPill";
import { fpPath } from "@/lib/site";

/**
 * Global Farming country picker (upgraded).
 *
 * Loads all countries from /api/countries, groups by UN region, offers a
 * search box + region tabs, and links each tile to
 *   /guides/global-farming/<code>
 * which the FarmersCountryProfile page resolves.
 */
const REGIONS = ["Africa", "Americas", "Asia", "Europe", "Oceania"];

export default function GlobalFarmingCountryPicker() {
  const [countries, setCountries] = useState([]);
  const [region, setRegion] = useState("All");
  const [query, setQuery] = useState("");
  const [nearMe, setNearMe] = useState(null);

  useEffect(() => {
    http.get("/countries")
      .then(({ data }) => setCountries(data.countries || []))
      .catch(() => setCountries([]));
    const loc = readLocation();
    if (loc?.country) setNearMe(loc.country.toLowerCase());
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return countries.filter((c) => {
      if (region !== "All" && c.region !== region) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q;
    });
  }, [countries, region, query]);

  const grouped = useMemo(() => {
    const g = {};
    for (const c of filtered) (g[c.subregion || c.region] ||= []).push(c);
    return g;
  }, [filtered]);

  const near = countries.find((c) => nearMe && c.name.toLowerCase() === nearMe);

  return (
    <section data-testid="global-farming-picker" className="mt-10 rounded-3xl border border-black/10 bg-white/90 p-6 md:p-8">
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#2D8659)] flex items-center gap-1.5">
        <Globe size={12} weight="fill" /> Explore by country · {countries.length} countries
      </div>
      <h2 className="mt-2 font-serif-display text-3xl md:text-4xl leading-tight tracking-tight text-[#151312]">
        Pick a country to open its Global Farming guide.
      </h2>

      {near && (
        <Link
          to={fpPath(`/guides/global-farming/${near.code.toLowerCase()}`)}
          data-testid={`gf-near-me-${near.code}`}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
        >
          Near me · {near.flag} {near.name} <ArrowRight size={11} weight="bold" />
        </Link>
      )}

      {/* Search + region tabs */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="gf-controls">
        <label className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A3830]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search country · name or code (e.g. Kenya, KE)…"
            className="w-full rounded-full border border-black/15 bg-white pl-9 pr-4 py-2 text-sm"
            data-testid="gf-search"
          />
        </label>
        <div className="flex gap-1 flex-wrap">
          {["All", ...REGIONS].map((r) => (
            <button
              key={r}
              onClick={() => setRegion(r)}
              data-testid={`gf-region-${r}`}
              className={`rounded-full border px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${region === r ? "bg-[color:var(--np-brand,#2D8659)] text-white border-transparent" : "border-black/15 bg-white text-[#241209] hover:bg-black/5"}`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Grouped grid */}
      <div className="mt-6 space-y-6" data-testid="gf-country-grid">
        {Object.entries(grouped).map(([sub, list]) => (
          <div key={sub}>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830]">{sub} · {list.length}</div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {list.map((c) => {
                const published = c.article_status === "published" || c.article_status === "ready_for_review";
                return (
                  <Link
                    key={c.code}
                    to={fpPath(`/guides/global-farming/${c.code.toLowerCase()}`)}
                    data-testid={`gf-country-${c.code}`}
                    className={`group flex items-center gap-2 rounded-2xl border p-3 transition hover:shadow-sm hover:-translate-y-0.5 ${published ? "border-[color:var(--np-brand,#2D8659)]/40 bg-emerald-50/40" : "border-black/10 bg-white"}`}
                    title={c.name}
                  >
                    <span className="text-2xl leading-none">{c.flag}</span>
                    <span className="flex-1 min-w-0 text-sm truncate">{c.name}</span>
                    <ArrowRight size={11} weight="bold" className="opacity-50 group-hover:opacity-100" />
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-black/15 bg-white/60 p-8 text-center text-sm text-[#3A3830]" data-testid="gf-empty">
            No countries match "{query}" in {region}.
          </div>
        )}
      </div>

      <p className="mt-6 text-[10px] font-mono-tech tracking-[0.24em] uppercase text-[#3A3830]">
        Green tiles have a published FarmersPulse country guide · plain tiles are under research.
      </p>
    </section>
  );
}
